<template>
  <div class="chart-time-filter">
    <label v-if="label" class="chart-time-filter__label">{{ label }}</label>

    <div class="chart-time-filter__row">
      <!-- Preset chips (relative ranges) -->
      <div class="chart-time-filter__chips">
        <button
          v-for="opt in presets"
          :key="opt.value"
          type="button"
          class="chart-time-filter__chip"
          :class="{ 'chart-time-filter__chip--active': activePreset === opt.value && !customOpen }"
          @click="selectPreset(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- Custom range toggle -->
      <button
        type="button"
        class="chart-time-filter__cal-btn"
        :class="{ 'chart-time-filter__cal-btn--active': customOpen }"
        title="Rango personalizado"
        @click="customOpen = !customOpen"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
    </div>

    <transition name="chart-time-cal">
      <div v-if="customOpen" class="chart-time-filter__custom">
        <!-- Any rolling window: "last N minutes/hours/days" -->
        <div class="chart-time-filter__relative">
          <span class="chart-time-filter__cal-label">Últimos</span>
          <div class="chart-time-filter__relative-inputs">
            <input
              v-model.number="relativeAmount"
              type="number"
              min="1"
              step="1"
              class="form-control chart-time-filter__input chart-time-filter__input--amount"
              @change="applyRelative"
              @keydown.enter.prevent="applyRelative"
            >
            <select
              v-model="relativeUnit"
              class="form-control chart-time-filter__input chart-time-filter__input--unit"
              @change="applyRelative"
            >
              <option v-for="unit in relativeUnits" :key="unit.value" :value="unit.value">
                {{ unit.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Fixed range, minute precision -->
        <div class="chart-time-filter__calendar">
          <div class="chart-time-filter__cal-field">
            <span class="chart-time-filter__cal-label">Desde</span>
            <flat-pickr
              :value="calendarFrom"
              :config="fromPickerConfig"
              class="form-control chart-time-filter__datepicker"
              placeholder="Fecha y hora de inicio"
              @on-change="(dates, dateStr) => onCalendarFrom(dateStr)"
            />
          </div>
          <div class="chart-time-filter__cal-field">
            <span class="chart-time-filter__cal-label">Hasta</span>
            <flat-pickr
              :value="to"
              :config="toPickerConfig"
              class="form-control chart-time-filter__datepicker"
              placeholder="Ahora"
              @on-change="(dates, dateStr) => onCalendarTo(dateStr)"
            />
          </div>
        </div>
      </div>
    </transition>

    <span v-if="inverted" class="chart-time-filter__error">
      La fecha final es anterior a la inicial
    </span>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import {
  OPEN_TIME_RANGE_FROM,
  RELATIVE_UNITS,
  buildRelativeTime,
  isInvertedRange,
  parseRelativeTime,
} from '../utils/timeUtils'

const TIME_PRESETS = [
  { value: 'now-5m', label: '5m' },
  { value: 'now-15m', label: '15m' },
  { value: 'now-1h', label: '1h' },
  { value: 'now-6h', label: '6h' },
  { value: 'now-24h', label: '24h' },
  { value: 'now-7d', label: '7d' },
  { value: 'now-30d', label: '30d' },
  { value: 'now-90d', label: '90d' },
  { value: OPEN_TIME_RANGE_FROM, label: 'Todo' },
]

const PICKER_FORMAT = 'Y-m-d H:i'

export default {
  name: 'ChartTimeFilter',

  components: { flatPickr },

  props: {
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    label: { type: String, default: 'Rango de tiempo' },
    t: { type: Function, default: null },
  },

  data() {
    return {
      customOpen: false,
      relativeAmount: 10,
      relativeUnit: 'm',
      presets: TIME_PRESETS,
      relativeUnits: RELATIVE_UNITS,
    }
  },

  computed: {
    activePreset() {
      return this.to ? '' : this.from
    },

    relativeValue() {
      return buildRelativeTime(this.relativeAmount, this.relativeUnit)
    },

    // The pickers only show absolute dates; a relative from leaves "Desde" empty.
    calendarFrom() {
      return this.from && !this.from.includes('now') && this.from !== OPEN_TIME_RANGE_FROM ? this.from : ''
    },

    fromPickerConfig() {
      return {
        enableTime: true,
        time_24hr: true,
        dateFormat: PICKER_FORMAT,
        allowInput: true,
        defaultHour: 0,
        defaultMinute: 0,
        maxDate: this.to || null,
      }
    },

    toPickerConfig() {
      return {
        enableTime: true,
        time_24hr: true,
        dateFormat: PICKER_FORMAT,
        allowInput: true,
        defaultHour: 23,
        defaultMinute: 59,
        minDate: this.calendarFrom || null,
      }
    },

    inverted() {
      return isInvertedRange(this.from, this.to)
    },
  },

  watch: {
    from: {
      immediate: true,
      handler(val) {
        // A value no chip represents opens the custom panel, so the user sees
        // where it came from. The open range is a chip of its own.
        if (!val || this.presets.some(p => p.value === val)) return
        const parsed = parseRelativeTime(val)
        if (parsed && ['m', 'h', 'd'].includes(parsed.unit)) {
          this.relativeAmount = parsed.amount
          this.relativeUnit = parsed.unit
        }
        this.customOpen = true
      },
    },
  },

  methods: {
    selectPreset(value) {
      this.customOpen = false
      this.$emit('update:from', value)
      this.$emit('update:to', '')
    },

    // Applied on commit (change / Enter), not per keystroke: typing "10" would
    // otherwise reload the chart for "1" first. Enter followed by blur fires
    // twice, so an unchanged value is skipped.
    applyRelative() {
      if (!this.relativeValue) return
      if (this.relativeValue === this.from && !this.to) return
      this.$emit('update:from', this.relativeValue)
      this.$emit('update:to', '')
    },

    onCalendarFrom(dateStr) {
      if (dateStr) this.$emit('update:from', dateStr)
    },

    onCalendarTo(dateStr) {
      this.$emit('update:to', dateStr || '')
    },
  },
}
</script>

<style scoped>
.chart-time-filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chart-time-filter__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

.chart-time-filter__row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-time-filter__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chart-time-filter__chip {
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.chart-time-filter__chip:hover {
  background: rgba(0, 104, 201, 0.2);
  border-color: rgba(0, 104, 201, 0.4);
}

.chart-time-filter__chip--active {
  background: #0068c9;
  border-color: #0068c9;
  color: #fff;
}

.chart-time-filter__cal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.chart-time-filter__cal-btn:hover {
  background: rgba(0, 104, 201, 0.2);
  border-color: rgba(0, 104, 201, 0.4);
}

.chart-time-filter__cal-btn--active {
  background: rgba(0, 104, 201, 0.25);
  border-color: #0068c9;
  color: #4da6ff;
}

/* Custom range panel: "Últimos" and Desde/Hasta share a row and wrap, in
   that order, when the panel is too narrow. */
.chart-time-filter__custom {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 16px;
  padding-top: 6px;
}

.chart-time-filter__relative {
  flex: 0 0 auto;
}

.chart-time-filter__relative-inputs {
  display: flex;
  gap: 6px;
  align-items: center;
}

.chart-time-filter__calendar {
  flex: 1 1 300px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
}

.chart-time-filter__cal-field {
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 240px;
}

.chart-time-filter__cal-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
  opacity: 0.55;
}

.chart-time-filter__datepicker,
.chart-time-filter__input {
  font-size: 0.82rem;
  padding: 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  min-height: 32px;
  width: 100%;
}

.chart-time-filter__input--amount {
  width: 80px;
}

.chart-time-filter__input--unit {
  width: 110px;
}

.chart-time-filter__input--unit option {
  color: #333;
}

.chart-time-filter__error {
  font-size: 0.72rem;
  color: #ea5455;
}

/* Light mode */
:root .charts-embedder:not(.charts-embedder--dark) .chart-time-filter__chip,
:root .charts-embedder:not(.charts-embedder--dark) .chart-time-filter__cal-btn,
:root .charts-embedder:not(.charts-embedder--dark) .chart-time-filter__input,
:root .charts-embedder:not(.charts-embedder--dark) .chart-time-filter__datepicker {
  border-color: rgba(0, 0, 0, 0.15);
  background: rgba(0, 0, 0, 0.03);
}

:root .charts-embedder:not(.charts-embedder--dark) .chart-time-filter__chip--active {
  background: #0068c9;
  border-color: #0068c9;
  color: #fff;
}

/* Panel slide transition */
.chart-time-cal-enter-active,
.chart-time-cal-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.chart-time-cal-enter,
.chart-time-cal-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
}

.chart-time-cal-enter-to,
.chart-time-cal-leave {
  max-height: 260px;
  opacity: 1;
}
</style>
